import {CTDJobStatus, CTDJob} from '@crema/services/apis/ctd';
import {AppActions} from '../../types';
import {
  CTD_UPLOAD_START,
  CTD_UPLOAD_SUCCESS,
  CTD_UPLOAD_FAILURE,
  CTD_POLL_UPDATE,
  CTD_POLL_STOP,
  CTD_FETCH_HISTORY_START,
  CTD_FETCH_HISTORY_SUCCESS,
  CTD_FETCH_HISTORY_FAILURE,
  CTD_CLEAR_CURRENT_JOB,
} from '../../types/actions/CTD.actions';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface CTDState {
  /** The job currently being tracked (null until the user submits a file). */
  currentJob: CTDJobStatus | null;
  /** True while the polling interval is expected to be running. */
  isPolling: boolean;
  /** All calibration jobs belonging to the authenticated user. */
  jobHistory: CTDJob[];
  /** True while the upload POST request is in flight. */
  isUploading: boolean;
  /** True while the job-history GET request is in flight. */
  isFetchingHistory: boolean;
  /** Module-level error message (null when there is no error). */
  error: string | null;
}

const INIT_STATE: CTDState = {
  currentJob: null,
  isPolling: false,
  jobHistory: [],
  isUploading: false,
  isFetchingHistory: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const CTD = (state: CTDState = INIT_STATE, action: AppActions): CTDState => {
  switch (action.type) {
    // --- Upload lifecycle ---
    case CTD_UPLOAD_START: {
      return {
        ...state,
        isUploading: true,
        error: null,
      };
    }

    case CTD_UPLOAD_SUCCESS: {
      return {
        ...state,
        isUploading: false,
        isPolling: true,
        // Seed currentJob with the initial status so the polling component
        // renders immediately without waiting for the first poll tick.
        currentJob: {
          job_id: action.payload.jobId,
          status: action.payload.status as CTDJobStatus['status'],
        } as CTDJobStatus,
        error: null,
      };
    }

    case CTD_UPLOAD_FAILURE: {
      return {
        ...state,
        isUploading: false,
        error: action.error,
      };
    }

    // --- Polling ---
    case CTD_POLL_UPDATE: {
      const pollable =
        action.payload.status === 'pending' ||
        action.payload.status === 'running';

      return {
        ...state,
        currentJob: action.payload,
        isPolling: pollable,
      };
    }

    case CTD_POLL_STOP: {
      return {
        ...state,
        isPolling: false,
      };
    }

    // --- History ---
    case CTD_FETCH_HISTORY_START: {
      return {
        ...state,
        isFetchingHistory: true,
      };
    }

    case CTD_FETCH_HISTORY_SUCCESS: {
      return {
        ...state,
        isFetchingHistory: false,
        jobHistory: action.payload,
      };
    }

    case CTD_FETCH_HISTORY_FAILURE: {
      return {
        ...state,
        isFetchingHistory: false,
        error: action.error,
      };
    }

    // --- Reset ---
    case CTD_CLEAR_CURRENT_JOB: {
      return {
        ...state,
        currentJob: null,
        isPolling: false,
        error: null,
      };
    }

    default:
      return state;
  }
};

export default CTD;
